import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  TextField,
  Autocomplete,
  IconButton,
  Icon,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import { dutyTypeList } from "constants/dutyType";
import { getDutyTypeList } from "actions/dutyType";
import { getShiftList } from "actions/shift";
import { shiftList } from "constants/shift";
import { getTimeSheetLhbRecords, getLhbRecordsList } from "actions/timesheet";
import { timesheetLhbRecords } from "constants/timesheet";
import { ISOToDDMMYYYY } from "components/CommonComponent/CommonFunction";
import { lhbRecordsList } from "constants/lhbRecords";

const STORAGE_KEY = "timesheet_draft";

const initialFields = {
  dutyType: { value: null, error: "" },
  shift: { value: null, error: "" },
  date: { value: dayjs(), error: "" },
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const s = status.toUpperCase();
  const map = {
    PENDING: { color: "secondary", label: "Pending" },
    APPROVED: { color: "success", label: "Approved" },
    REJECTED: { color: "error", label: "Rejected" },
  };
  const config = map[s] || { color: "secondary", label: s };
  return (
    <MDBadge
      badgeContent={config.label}
      color={config.color}
      variant="gradient"
      size="sm"
      circular
    />
  );
};
StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

function TimeSheetListing() {
  const user = JSON.parse(localStorage.getItem("pt") || "{}");

  const dispatch = useDispatch();
  const timesheetListData = useSelector(
    (state) => state.timesheet.timesheetLhbRecords,
  );
  //duty type listing
  const dutyTypeListData = useSelector((state) => state.dutyType.dutyTypeList);
  //shift listing
  const shiftListData = useSelector((state) => state.shift.shiftList);
  const lhbRecordsListData = useSelector(
    (state) => state.timesheet.lhbRecordsList,
  );
  const navigate = useNavigate();
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedLHB, setSelectedLHB] = useState(null);
  const [entryData, setEntryData] = useState(initialFields);
  const [selectedDutyTab, setSelectedDutyTab] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [openDate, setOpenDate] = useState(false);

  const dutyTypes = dutyTypeListData?.data?.DutyType || [];
  const shifts = (shiftListData?.data?.shift || [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  //to get only active duty types
  const activeDutyTypes =
    dutyTypes.filter((d) => d.status === "ACTIVE").length > 0
      ? dutyTypes.filter((d) => d.status === "ACTIVE")
      : dutyTypes;

  //to get the duty type list other than regularu
  const otherDuties = activeDutyTypes.filter((d) => d.name !== "Regular");

  //to get the name of duty type and show the shift according to it
  const selectedDutyTypeName = entryData.dutyType?.value?.name;
  const showShifts =
    selectedDutyTab === "Regular" ||
    selectedDutyTab === "Others" ||
    ["Half Day", "Outdoor", "Overtime"].includes(selectedDutyTypeName);

  //condition to check shift is required or not -> when regular or others then shift is required
  const isShiftRequired =
    selectedDutyTab === "Regular" ||
    selectedDutyTab === "Others" ||
    ["Half Day", "Outdoor", "Overtime"].includes(selectedDutyTypeName);
  const isShiftValid = isShiftRequired
    ? Boolean(entryData.shift?.value?.ID)
    : true;

  const isDutyTypeSelected = Boolean(entryData.dutyType?.value?.ID);
  const isDateValid = Boolean(entryData.date?.value);

  //get the lhb no list for dropdown with name ,shift and date
  const lhbOptions = lhbRecordsListData?.data
    ? [
        ...(lhbRecordsListData.data.myLHB || []),
        ...(lhbRecordsListData.data.othersLHB || []),
      ].map((item) => ({
        lhbNo: item.lhb_no,
        name: item.user_name,
        shiftName: item.shift_name,
        date: ISOToDDMMYYYY(item.shift_date),
      }))
    : [];

  useEffect(() => {
    const draft = localStorage.getItem(STORAGE_KEY);
    const parsedData = JSON.parse(draft);

    if (draft && parsedData?.employeeId === user?.employeeId) {
      setPopupOpen(false);
      navigate(`/technician/add-new?mode=add`);
    } else {
      // First time show shift popup
      setPopupOpen(true);
    }
  }, []);

  useEffect(() => {
    getDutyTypeList(dispatch);
    getShiftList(dispatch);
    getLhbRecordsList(dispatch);
    return () => {
      dispatch({ type: dutyTypeList, payload: {} });
      dispatch({ type: shiftList, payload: {} });
      dispatch({ type: lhbRecordsList, payload: {} });
    };
  }, [dispatch]);

  useEffect(() => {
    getTimeSheetLhbRecords(dispatch, pageNum);
    return () => {
      dispatch({ type: timesheetLhbRecords, payload: {} });
    };
  }, [dispatch]);

  //check the listing data state using code and message
  useEffect(() => {
    if (timesheetListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: timesheetListData?.data?.timesheet?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (timesheetListData?.code || timesheetListData?.message) {
      setListState({
        isLoading: false,
        error:
          timesheetListData?.message ||
          "Failed to load timesheet entries list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [timesheetListData]);

  const formattedListData = timesheetListData?.data?.timesheet?.map(
    (item, index) => ({
      id: item.ID,
      status: item?.status || "-",
      date: ISOToDDMMYYYY(item?.shiftStartDate),
      // hours: item?.totalDurationMinutes
      //   ? `${item.totalDurationMinutes / 60} hrs`
      //   : "-",
      hours: item?.formattedHours,
      lhbNo: item?.lhbRecords?.[0]?.ID || "-",
      srNo:
        (pageNum - 1) * (timesheetListData?.data?.noOfRecordsPerPage || 10) +
        index +
        1,
    }),
  );

  const columns = [
    {
      Header: "Sr No.",
      accessor: "srNo",
      align: "left",
      width: "135px",
    },
    {
      Header: "Date",
      accessor: "date",
      align: "left",
    },
    {
      Header: "Hours",
      accessor: "hours",
      align: "left",
    },
    {
      Header: "Status",
      accessor: "status",
      align: "left",
      Cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      Header: "Action",
      accessor: "actions",
      align: "center",
      disableSortBy: true,
      Cell: (row) => (
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
          pr={3}
        >
          <Tooltip title="View" arrow>
            <Icon
              sx={({ palette }) => ({
                cursor: "pointer",
                color: palette.secondary.main,
              })}
              onClick={() => handleView(row)}
            >
              visibility
            </Icon>
          </Tooltip>
        </MDBox>
      ),
    },
  ];

  const handleAddClick = () => {
    const draft = localStorage.getItem(STORAGE_KEY);

    if (draft) {
      setPopupOpen(false);
      navigate(
        `/technician/add-new?mode=add&shiftId=${entryData.shift.value?.ID}&dutyTypeId=${entryData.dutyType.value?.ID}&date=${entryData.date.value?.format("DD-MM-YYYY")}`,
      );
    } else {
      setPopupOpen(true);
      setSelectedLHB(null);
    }
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  const handleNo = () => {
    setPopupOpen(false);
    navigate(
      `/technician/add-new?mode=add&shiftId=${entryData.shift.value?.ID}&dutyTypeId=${entryData.dutyType.value?.ID}&date=${entryData.date.value?.format("DD-MM-YYYY")}`,
    );
  };

  const handleProceed = () => {
    if (selectedLHB) {
      navigate(
        `/technician/add-new?lhbNo=${selectedLHB.lhbNo}&mode=add&shiftId=${entryData.shift.value?.ID}&dutyTypeId=${entryData.dutyType.value?.ID}&date=${entryData.date.value?.format("DD-MM-YYYY")}`,
      );
      setPopupOpen(false);
    }
  };

  const handleView = (row) => {
    const timesheetId = row?.id;
    if (!timesheetId) {
      return;
    }
    const shiftParam = entryData.shift.value?.ID
      ? `&shiftId=${entryData.shift.value.ID}`
      : "";

    const dutyTypeParam = entryData.dutyType.value?.ID
      ? `&dutyTypeId=${entryData.dutyType.value.ID}`
      : "";
    navigate(
      `/technician/add-new?id=${timesheetId}&mode=view${shiftParam}${dutyTypeParam}`,
    );
  };
  const handleChange = (key, value) => {
    setEntryData((prev) => ({
      ...prev,
      [key]: {
        value,
        error: "",
      },
    }));
  };

  const handleDutyTabClick = (type) => {
    setSelectedDutyTab(type);
    if (type === "Regular") {
      const regular = dutyTypes.find((d) => d.name === "Regular");
      handleChange("dutyType", regular || null);
    } else {
      handleChange("dutyType", null);
    }
    handleChange("shift", null);
  };

  return (
    <MDBox mt={4}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              px={3}
              pt={3}
              pb={2}
              display="flex"
              justifyContent="space-between"
            >
              <MDTypography variant="h5" fontWeight="bold">
                My Timesheets
              </MDTypography>

              <MDButton
                variant="contained"
                color="deepBlue"
                sx={{
                  py: 1.2,
                  px: 3,
                  fontWeight: "bold",
                  textTransform: "none",
                }}
                onClick={handleAddClick}
              >
                Add New Timesheet
              </MDButton>
            </MDBox>

            <MDBox
              px={3}
              py={1}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="body2">
                Total Records: {timesheetListData?.data?.totalRecords || 0}
              </MDTypography>
            </MDBox>

            <MDBox sx={{ width: "100%", overflowX: "auto" }}>
              {listState.isLoading ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="150px"
                >
                  <i className="fa fa-spinner fa-spin fa-2xl loader" />
                </MDBox>
              ) : listState.error ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="150px"
                >
                  <MDTypography variant="body2" color="error">
                    {listState.error}
                  </MDTypography>
                </MDBox>
              ) : listState.noRecords ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="150px"
                >
                  <MDTypography variant="body2" color="text">
                    No timesheet found.
                  </MDTypography>
                </MDBox>
              ) : (
                <DataTable
                  columns={columns}
                  data={formattedListData || []}
                  isSorted={true}
                  pageNum={pageNum}
                  setPageNum={setPageNum}
                  totalPages={Math.ceil(
                    timesheetListData?.data?.totalRecords /
                      timesheetListData?.data?.noOfRecordsPerPage,
                  )}
                  onRowClick={(row) => handleView(row)}
                />
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <CenterPopup
        isOpen={popupOpen}
        onClose={handleClosePopup}
        title="Add Time Sheet"
        width={560}
      >
        <MDBox position="relative" px={2} pb={2}>
          {/* Close Icon */}
          <IconButton
            onClick={handleClosePopup}
            sx={{
              position: "absolute",
              top: -21,
              right: 0,
            }}
          >
            <CloseIcon />
          </IconButton>

          <MDBox textAlign="center" mb={3}>
            <MDTypography variant="h5" fontWeight="medium" mb={1}>
              Select Shift
            </MDTypography>

            <MDBox display="flex" justifyContent="center" gap={1}>
              {["Regular", "Others"].map((type) => (
                <MDBox
                  key={type}
                  onClick={() => handleDutyTabClick(type)}
                  sx={{
                    px: 2.5,
                    py: 0.8,
                    borderRadius: "1.25rem",
                    cursor: "pointer",
                    border: "1px solid",
                    // borderColor: selectedDutyTab === type ? "#003D7F" : "#ccc",
                    backgroundColor:
                      selectedDutyTab === type ? "#003D7F" : "transparent",
                    color: selectedDutyTab === type ? "#fff" : "#003D7F",
                  }}
                >
                  {type}
                </MDBox>
              ))}
            </MDBox>

            <MDBox
              mt={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              {selectedDutyTab === "Others" && (
                <>
                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    {otherDuties.map((item) => (
                      <MDBox
                        key={item.ID}
                        onClick={() => handleChange("dutyType", item)}
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: "1.25rem",
                          border: "1px solid",
                          cursor: "pointer",
                          background:
                            entryData.dutyType?.value?.ID === item.ID
                              ? "#003D7F"
                              : "transparent",
                          color:
                            entryData.dutyType?.value?.ID === item.ID
                              ? "#fff"
                              : "#003D7F",
                        }}
                      >
                        {item.name}
                      </MDBox>
                    ))}
                  </MDBox>
                </>
              )}

              {showShifts && (
                <MDBox
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  justifyContent="center"
                >
                  {shifts.map((item) => {
                    const isDutySelected = Boolean(
                      entryData.dutyType?.value?.ID,
                    );

                    return (
                      <MDBox
                        key={item.ID}
                        onClick={() => {
                          if (!isDutySelected) return; // prevent click
                          handleChange("shift", item);
                        }}
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: "1.25rem",
                          border: "1px solid",
                          cursor: isDutySelected ? "pointer" : "not-allowed",
                          background:
                            entryData.shift?.value?.ID === item.ID
                              ? "#003D7F"
                              : "transparent",
                          color:
                            entryData.shift?.value?.ID === item.ID
                              ? "#fff"
                              : "#003D7F",
                          opacity: isDutySelected ? 1 : 0.5,
                        }}
                      >
                        {item.name}
                      </MDBox>
                    );
                  })}
                </MDBox>
              )}
            </MDBox>
          </MDBox>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MDBox mt={2} width="220px" mx="auto">
              <DatePicker
                disableFuture
                open={openDate}
                onOpen={() => setOpenDate(true)}
                onClose={() => setOpenDate(false)}
                value={entryData.date?.value}
                onChange={(value) => handleChange("date", value)}
                shouldDisableDate={(date) => {
                  const diff = dayjs()
                    .startOf("day")
                    .diff(date.startOf("day"), "day");
                  return diff < 0 || diff > 2;
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    onClick: () => setOpenDate(true),
                    sx: {
                      "& input": {
                        fontSize: "1rem",
                        cursor: "pointer",
                      },
                    },
                  },
                }}
                format="DD/MM/YYYY"
              />
            </MDBox>
          </LocalizationProvider>

          {/* Divider */}
          <MDBox
            sx={{
              borderBottom: "1px solid #eee",
              mb: 3,
            }}
          />

          <MDBox
            p={3}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              background: "#fafafa",
              textAlign: "center",
            }}
          >
            <MDTypography variant="h5" fontWeight="medium" mb={2}>
              From Existing Timesheet
            </MDTypography>

            <RequiredLabel>LHB No.</RequiredLabel>

            <Autocomplete
              options={lhbOptions}
              getOptionLabel={(option) =>
                `${option.lhbNo} - ${option.name || ""} (${option.shiftName || "-"} - ${option.date || "-"})`
              }
              value={selectedLHB}
              onChange={(event, value) => setSelectedLHB(value)}
              isOptionEqualToValue={(option, value) =>
                option.lhbNo === value.lhbNo
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Select LHB No" />
              )}
              sx={{ mb: 2 }}
            />

            <MDButton
              variant="contained"
              color="deepBlue"
              fullWidth
              disabled={
                !selectedLHB ||
                !isShiftValid ||
                !isDutyTypeSelected ||
                !isDateValid
              }
              onClick={handleProceed}
              sx={{
                px: 2,
                py: 1,
              }}
            >
              Proceed
            </MDButton>
          </MDBox>

          {/* OR */}
          <MDBox textAlign="center" my={3}>
            <MDTypography variant="caption" color="text">
              OR
            </MDTypography>
          </MDBox>

          {/* New Timesheet */}
          <MDBox
            p={3}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              textAlign: "center",
              background: "#fafafa",
            }}
          >
            <MDTypography variant="h5" fontWeight="medium" mb={4}>
              New Timesheet
            </MDTypography>
            <MDButton
              variant="contained"
              color="deepBlue"
              disabled={!isShiftValid || !isDutyTypeSelected || !isDateValid}
              onClick={handleNo}
              fullWidth
              sx={{
                px: 2,
                py: 1,
              }}
            >
              Proceed
            </MDButton>
          </MDBox>
        </MDBox>
      </CenterPopup>
    </MDBox>
  );
}

export default TimeSheetListing;
